import { Component } from '@theme/component';

const DATE_FORMAT = /** @type {const} */ ({
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  numberingSystem: 'latn',
});

/**
 * Rewrites the delivery timeline dates from the visitor's current date in the page language. Offsets are
 * working days; with `data-skip-fridays="true"` Fridays are not counted.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} dates - Date elements with `data-offset-min` and `data-offset-max` day offsets.
 *
 * @extends Component<Refs>
 */
class ZayaDeliveryEstimate extends Component {
  requiredRefs = ['dates'];

  connectedCallback() {
    super.connectedCallback();
    this.#renderDates();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#renderDates();
  }

  #renderDates() {
    /** @type {Intl.DateTimeFormat} */
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, DATE_FORMAT);
    } catch {
      // Keep the server-rendered dates when the page language isn't supported.
      return;
    }

    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const skipFridays = this.dataset.skipFridays === 'true';

    for (const element of this.refs.dates) {
      const min = Number(element.dataset.offsetMin);
      const max = Math.max(min, Number(element.dataset.offsetMax));
      if (!Number.isFinite(min) || !Number.isFinite(max)) continue;

      const start = addWorkingDays(today, min, skipFridays);
      const end = addWorkingDays(today, max, skipFridays);
      if (min === max) {
        element.textContent = formatter.format(start);
      } else if (typeof formatter.formatRange === 'function') {
        element.textContent = formatter.formatRange(start, end);
      } else {
        element.textContent = `${formatter.format(start)} – ${formatter.format(end)}`;
      }
    }
  }
}

const FRIDAY = 5;

/**
 * Moves forward by a number of working days, optionally not counting Fridays.
 * @param {Date} date
 * @param {number} days
 * @param {boolean} skipFridays
 * @returns {Date}
 */
function addWorkingDays(date, days, skipFridays) {
  const result = new Date(date);
  let counted = 0;
  while (counted < days) {
    result.setDate(result.getDate() + 1);
    if (skipFridays && result.getDay() === FRIDAY) continue;
    counted++;
  }
  return result;
}

if (!customElements.get('zaya-delivery-estimate')) {
  customElements.define('zaya-delivery-estimate', ZayaDeliveryEstimate);
}
